import { z } from 'zod';

import { auditFieldsSchema, entityIdSchema, pageMetaSchema } from './common.js';

export const LOGISTICS_MAX_MILLIUNITS = 9_000_000_000_000_000;
export const LOGISTICS_MIN_MILLICELSIUS = -100_000;
export const LOGISTICS_MAX_MILLICELSIUS = 100_000;

export const logisticsCanonicalIsoInstantSchema = z.iso
  .datetime({ offset: true })
  .refine(
    (value) => value.endsWith('Z') && new Date(value).toISOString() === value,
    'La date doit être un instant UTC ISO canonique.',
  );

export const logisticsArchiveStateSchema = z.enum(['active', 'archived', 'all']);
export const supplierCategorySchema = z.enum(['general', 'fish', 'both']);
export const supplierStatusSchema = z.enum(['active', 'archived']);
export const supplyCategorySchema = z.enum(['fish', 'general', 'fuel', 'other']);
export const supplyUnitSchema = z.enum(['unit', 'package', 'kg', 'g', 'l', 'ml']);
export const supplySettlementStatusSchema = z.enum(['unpaid', 'paid']);
export const coldChainPeriodSchema = z.enum(['morning', 'evening', 'other']);
export const coldChainAlertSchema = z.enum(['TOO_COLD', 'TOO_WARM', 'NORMAL', 'NO_READING']);

export const logisticsQuantityMilliunitsSchema = z
  .number()
  .int()
  .positive()
  .max(LOGISTICS_MAX_MILLIUNITS);
export const logisticsWeightGramsSchema = z.number().int().positive().max(LOGISTICS_MAX_MILLIUNITS);
export const logisticsTemperatureMilliCelsiusSchema = z
  .number()
  .int()
  .min(LOGISTICS_MIN_MILLICELSIUS)
  .max(LOGISTICS_MAX_MILLICELSIUS);
export const logisticsMoneyCentsSchema = z
  .number()
  .int()
  .nonnegative()
  .max(LOGISTICS_MAX_MILLIUNITS);

const supplierFieldsSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    category: supplierCategorySchema,
    email: z.union([z.email().max(254), z.null()]).default(null),
    phone: z.union([z.string().trim().min(1).max(40), z.null()]).default(null),
    notes: z.string().trim().max(2_000).default(''),
  })
  .strict();

export const supplierCreateInputSchema = supplierFieldsSchema;
export const supplierUpdateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    category: supplierCategorySchema.optional(),
    email: z.union([z.email().max(254), z.null()]).optional(),
    phone: z.union([z.string().trim().min(1).max(40), z.null()]).optional(),
    notes: z.string().trim().max(2_000).optional(),
    revision: z.number().int().positive(),
  })
  .strict();
export const logisticsRevisionInputSchema = z
  .object({ revision: z.number().int().positive() })
  .strict();
export const supplierSchema = supplierFieldsSchema
  .extend({
    id: entityIdSchema,
    status: supplierStatusSchema,
    archivedAt: logisticsCanonicalIsoInstantSchema.nullable(),
    archivedBy: entityIdSchema.nullable(),
    ...auditFieldsSchema.shape,
  })
  .strict();

const cursorPayloadSchema = z
  .object({
    kind: z.enum(['supplier', 'supply', 'device', 'reading', 'relation']),
    value: z.string().min(1).max(4_000),
    id: entityIdSchema,
  })
  .strict();

export type LogisticsCursor = z.infer<typeof cursorPayloadSchema>;

export function encodeLogisticsCursor(value: LogisticsCursor): string {
  return encodeURIComponent(JSON.stringify(cursorPayloadSchema.parse(value)));
}

export function decodeLogisticsCursor(
  cursor: string,
  expectedKind?: LogisticsCursor['kind'],
): LogisticsCursor | null {
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(cursor));
    const result = cursorPayloadSchema.safeParse(parsed);
    if (!result.success || (expectedKind !== undefined && result.data.kind !== expectedKind)) {
      return null;
    }
    return result.data;
  } catch {
    return null;
  }
}

function cursorSchema(kind: LogisticsCursor['kind']) {
  return z
    .string()
    .trim()
    .min(1)
    .refine((value) => decodeLogisticsCursor(value, kind) !== null, 'Curseur Logistique invalide.');
}

const logisticsListBase = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).optional(),
});

export const supplierListQuerySchema = logisticsListBase
  .extend({
    cursor: cursorSchema('supplier').optional(),
    state: logisticsArchiveStateSchema.default('active'),
  })
  .strict();
export const supplierCountersSchema = z
  .object({
    filtered: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    archived: z.number().int().nonnegative(),
  })
  .strict();
export const supplierPageSchema = z
  .object({
    items: z.array(supplierSchema),
    page: pageMetaSchema,
    counters: supplierCountersSchema,
  })
  .strict();

export const supplierSummarySchema = z
  .object({ id: entityIdSchema, name: z.string().min(1), status: supplierStatusSchema })
  .strict();
export const logisticsStaySummarySchema = z
  .object({
    id: entityIdSchema,
    label: z.string().min(1),
    status: z.string().min(1),
  })
  .strict();
export const logisticsDocumentSummarySchema = z
  .object({
    id: entityIdSchema,
    filename: z.string().min(1),
    purpose: z.enum(['attachment', 'child_justification', 'gift_voucher']),
  })
  .strict();

const supplyEntryFieldsSchema = z
  .object({
    supplierId: entityIdSchema.nullable().default(null),
    stayId: entityIdSchema.nullable().default(null),
    category: supplyCategorySchema,
    label: z.string().trim().min(1).max(240),
    quantityMilliunits: logisticsQuantityMilliunitsSchema,
    unit: supplyUnitSchema,
    weightGrams: logisticsWeightGramsSchema.nullable().default(null),
    priceCents: logisticsMoneyCentsSchema,
    receivedAt: logisticsCanonicalIsoInstantSchema,
    temperatureMilliCelsius: logisticsTemperatureMilliCelsiusSchema.nullable().default(null),
    ticketDocumentId: entityIdSchema.nullable().default(null),
    notes: z.string().trim().max(2_000).default(''),
  })
  .strict();

function requireFishTemperature(
  value: {
    category?: z.infer<typeof supplyCategorySchema> | undefined;
    temperatureMilliCelsius?: number | null | undefined;
  },
  context: z.RefinementCtx,
): void {
  if (value.category === 'fish' && value.temperatureMilliCelsius === null) {
    context.addIssue({
      code: 'custom',
      path: ['temperatureMilliCelsius'],
      message: 'Une température est obligatoire pour le poisson.',
    });
  }
}

export const supplyEntryCreateInputSchema =
  supplyEntryFieldsSchema.superRefine(requireFishTemperature);
export const supplyEntryUpdateInputSchema = z
  .object({
    supplierId: entityIdSchema.nullable().optional(),
    stayId: entityIdSchema.nullable().optional(),
    category: supplyCategorySchema.optional(),
    label: z.string().trim().min(1).max(240).optional(),
    quantityMilliunits: logisticsQuantityMilliunitsSchema.optional(),
    unit: supplyUnitSchema.optional(),
    weightGrams: logisticsWeightGramsSchema.nullable().optional(),
    priceCents: logisticsMoneyCentsSchema.optional(),
    receivedAt: logisticsCanonicalIsoInstantSchema.optional(),
    temperatureMilliCelsius: logisticsTemperatureMilliCelsiusSchema.nullable().optional(),
    ticketDocumentId: entityIdSchema.nullable().optional(),
    notes: z.string().trim().max(2_000).optional(),
    revision: z.number().int().positive(),
  })
  .strict()
  .superRefine(requireFishTemperature);
export const supplySettlementInputSchema = logisticsRevisionInputSchema;

export const supplyEntrySchema = supplyEntryFieldsSchema
  .extend({
    id: entityIdSchema,
    supplier: supplierSummarySchema.nullable(),
    stay: logisticsStaySummarySchema.nullable(),
    ticketDocument: logisticsDocumentSummarySchema.nullable(),
    settlementStatus: supplySettlementStatusSchema,
    paidAt: logisticsCanonicalIsoInstantSchema.nullable(),
    paidBy: entityIdSchema.nullable(),
    archivedAt: logisticsCanonicalIsoInstantSchema.nullable(),
    archivedBy: entityIdSchema.nullable(),
    ...auditFieldsSchema.shape,
  })
  .strict()
  .superRefine(requireFishTemperature);

export const supplyEntryListQuerySchema = logisticsListBase
  .extend({
    cursor: cursorSchema('supply').optional(),
    state: logisticsArchiveStateSchema.default('active'),
    category: supplyCategorySchema.optional(),
    settlementStatus: supplySettlementStatusSchema.optional(),
    supplierId: entityIdSchema.optional(),
    stayId: entityIdSchema.optional(),
  })
  .strict();
export const supplyEntryCountersSchema = z
  .object({
    filtered: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    archived: z.number().int().nonnegative(),
    unpaid: z.number().int().nonnegative(),
    paid: z.number().int().nonnegative(),
    totalCents: logisticsMoneyCentsSchema,
  })
  .strict();
export const supplyEntryPageSchema = z
  .object({
    items: z.array(supplyEntrySchema),
    page: pageMetaSchema,
    counters: supplyEntryCountersSchema,
  })
  .strict();

const coldChainDeviceFieldsSchema = z
  .object({
    name: z.string().trim().min(1).max(160),
    location: z.string().trim().max(200).default(''),
    minimumMilliCelsius: logisticsTemperatureMilliCelsiusSchema,
    maximumMilliCelsius: logisticsTemperatureMilliCelsiusSchema,
  })
  .strict();

function validateColdChainThresholds(
  value: {
    minimumMilliCelsius?: number | undefined;
    maximumMilliCelsius?: number | undefined;
  },
  context: z.RefinementCtx,
): void {
  if (
    value.minimumMilliCelsius !== undefined &&
    value.maximumMilliCelsius !== undefined &&
    value.maximumMilliCelsius < value.minimumMilliCelsius
  ) {
    context.addIssue({
      code: 'custom',
      path: ['maximumMilliCelsius'],
      message: 'Le maximum doit être supérieur ou égal au minimum.',
    });
  }
}

export const coldChainDeviceCreateInputSchema = coldChainDeviceFieldsSchema.superRefine(
  validateColdChainThresholds,
);
export const coldChainDeviceUpdateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(160).optional(),
    location: z.string().trim().max(200).optional(),
    minimumMilliCelsius: logisticsTemperatureMilliCelsiusSchema.optional(),
    maximumMilliCelsius: logisticsTemperatureMilliCelsiusSchema.optional(),
    revision: z.number().int().positive(),
  })
  .strict()
  .superRefine(validateColdChainThresholds);

export const coldChainReadingSchema = z
  .object({
    id: entityIdSchema,
    deviceId: entityIdSchema,
    measuredAt: logisticsCanonicalIsoInstantSchema,
    period: coldChainPeriodSchema,
    temperatureMilliCelsius: logisticsTemperatureMilliCelsiusSchema,
    thresholdMinimumSnapshot: logisticsTemperatureMilliCelsiusSchema,
    thresholdMaximumSnapshot: logisticsTemperatureMilliCelsiusSchema,
    snapshotAlert: coldChainAlertSchema.exclude(['NO_READING']),
    authorId: entityIdSchema,
    comment: z.string().max(2_000),
    createdAt: logisticsCanonicalIsoInstantSchema,
  })
  .strict();
export const coldChainReadingCreateInputSchema = z
  .object({
    measuredAt: logisticsCanonicalIsoInstantSchema,
    period: coldChainPeriodSchema,
    temperatureMilliCelsius: logisticsTemperatureMilliCelsiusSchema,
    comment: z.string().trim().max(2_000).default(''),
  })
  .strict();
export const coldChainReadingListQuerySchema = z
  .object({
    cursor: cursorSchema('reading').optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict();
export const coldChainReadingPageSchema = z
  .object({ items: z.array(coldChainReadingSchema), page: pageMetaSchema })
  .strict();

export const coldChainDeviceSchema = coldChainDeviceFieldsSchema
  .extend({
    id: entityIdSchema,
    status: supplierStatusSchema,
    currentAlert: coldChainAlertSchema,
    latestReading: coldChainReadingSchema.nullable(),
    archivedAt: logisticsCanonicalIsoInstantSchema.nullable(),
    archivedBy: entityIdSchema.nullable(),
    ...auditFieldsSchema.shape,
  })
  .strict()
  .superRefine(validateColdChainThresholds);
export const coldChainDeviceListQuerySchema = logisticsListBase
  .extend({
    cursor: cursorSchema('device').optional(),
    state: logisticsArchiveStateSchema.default('active'),
    alert: coldChainAlertSchema.optional(),
  })
  .strict();
export const coldChainDeviceCountersSchema = z
  .object({
    filtered: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    archived: z.number().int().nonnegative(),
    tooCold: z.number().int().nonnegative(),
    tooWarm: z.number().int().nonnegative(),
    normal: z.number().int().nonnegative(),
    noReading: z.number().int().nonnegative(),
  })
  .strict();
export const coldChainDevicePageSchema = z
  .object({
    items: z.array(coldChainDeviceSchema),
    page: pageMetaSchema,
    counters: coldChainDeviceCountersSchema,
  })
  .strict();

export const logisticsRelationOptionSchema = z
  .object({
    id: entityIdSchema,
    label: z.string().min(1),
    description: z.string(),
    available: z.boolean(),
  })
  .strict();
export const logisticsRelationQuerySchema = logisticsListBase
  .extend({ cursor: cursorSchema('relation').optional() })
  .strict();
export const logisticsRelationPageSchema = z
  .object({ items: z.array(logisticsRelationOptionSchema), page: pageMetaSchema })
  .strict();

export const logisticsErrorCodeSchema = z.enum([
  'LOGISTICS_IDEMPOTENCY_CONFLICT',
  'SUPPLIER_NOT_FOUND',
  'SUPPLIER_ARCHIVED',
  'SUPPLIER_NAME_CONFLICT',
  'SUPPLIER_REVISION_CONFLICT',
  'SUPPLY_ENTRY_NOT_FOUND',
  'SUPPLY_REVISION_CONFLICT',
  'SUPPLY_RELATION_INVALID',
  'SUPPLY_ALREADY_PAID',
  'COLD_DEVICE_NOT_FOUND',
  'COLD_DEVICE_ARCHIVED',
  'COLD_DEVICE_NAME_CONFLICT',
  'COLD_DEVICE_REVISION_CONFLICT',
  'COLD_THRESHOLD_INVALID',
]);

export type Supplier = z.infer<typeof supplierSchema>;
export type SupplierCreateInput = z.infer<typeof supplierCreateInputSchema>;
export type SupplierUpdateInput = z.infer<typeof supplierUpdateInputSchema>;
export type SupplierListQuery = z.infer<typeof supplierListQuerySchema>;
export type SupplyEntry = z.infer<typeof supplyEntrySchema>;
export type SupplyEntryCreateInput = z.infer<typeof supplyEntryCreateInputSchema>;
export type SupplyEntryUpdateInput = z.infer<typeof supplyEntryUpdateInputSchema>;
export type SupplyEntryListQuery = z.infer<typeof supplyEntryListQuerySchema>;
export type ColdChainDevice = z.infer<typeof coldChainDeviceSchema>;
export type ColdChainDeviceCreateInput = z.infer<typeof coldChainDeviceCreateInputSchema>;
export type ColdChainDeviceUpdateInput = z.infer<typeof coldChainDeviceUpdateInputSchema>;
export type ColdChainDeviceListQuery = z.infer<typeof coldChainDeviceListQuerySchema>;
export type ColdChainReading = z.infer<typeof coldChainReadingSchema>;
export type ColdChainReadingCreateInput = z.infer<typeof coldChainReadingCreateInputSchema>;
export type ColdChainReadingListQuery = z.infer<typeof coldChainReadingListQuerySchema>;
export type ColdChainAlert = z.infer<typeof coldChainAlertSchema>;
export type LogisticsRelationOption = z.infer<typeof logisticsRelationOptionSchema>;
export type LogisticsRelationQuery = z.infer<typeof logisticsRelationQuerySchema>;
export type LogisticsErrorCode = z.infer<typeof logisticsErrorCodeSchema>;
